import { makeAutoObservable, runInAction, reaction, computed } from 'mobx'
import {
  getFirestore,
  collection,
  doc,
  setDoc,
  serverTimestamp,
  writeBatch,
  arrayRemove,
  deleteField,
} from 'firebase/firestore'
import { getCollection } from '../../../shared/api/firestore'
import FirebaseApp from '../../../shared/api/firebase'

const db = getFirestore(FirebaseApp)

// Default schedule for a new staff member
const defaultWorkSchedule = {
  workDays: {
    mon: { start: '09:00', end: '18:00', isDayOff: false, breaks: [] },
    tue: { start: '09:00', end: '18:00', isDayOff: false, breaks: [] },
    wed: { start: '09:00', end: '18:00', isDayOff: false, breaks: [] },
    thu: { start: '09:00', end: '18:00', isDayOff: false, breaks: [] },
    fri: { start: '09:00', end: '18:00', isDayOff: false, breaks: [] },
    sat: { isDayOff: true, breaks: [] },
    sun: { isDayOff: true, breaks: [] },
  },
}

class StaffStore {
  staff = []
  isLoading = false

  constructor(rootStore) {
    makeAutoObservable(
      this,
      {
        rootStore: false,
        ownerStaff: computed,
        ownerStaffId: computed,
      },
      { autoBind: true },
    )
    this.rootStore = rootStore
    this.organizationStore = rootStore.organizationStore
    this.errorStore = rootStore.errorStore

    reaction(
      () => this.organizationStore.organization?.id,
      (orgId) => {
        if (orgId) {
          this.fetchStaff(orgId)
        } else {
          this.clearStaff()
        }
      },
      { fireImmediately: true },
    )
  }

  clearStaff() {
    this.staff = []
  }

  async _getStaffList(organizationId) {
    const staffList = await getCollection([
      'organizations',
      organizationId,
      'staff',
    ])

    for (const staffMember of staffList) {
      const exceptions = await getCollection([
        'organizations',
        organizationId,
        'staff',
        staffMember.id,
        'exceptions',
      ])
      staffMember.exceptions = exceptions || []
    }
    return staffList
  }

  async fetchStaff(organizationId) {
    if (!organizationId) return
    this.isLoading = true
    try {
      const staffList = await this._getStaffList(organizationId)
      runInAction(() => {
        this.staff = staffList
      })
    } catch (error) {
      console.error('Не удалось загрузить сотрудников:', error)
      this.errorStore.showError('Не удалось загрузить список сотрудников')
    } finally {
      runInAction(() => {
        this.isLoading = false
      })
    }
  }

  async inviteStaffMember(staffData) {
    const orgId = this.organizationStore.organization.id
    if (!orgId || !staffData.email || !staffData.name) {
      this.errorStore.showError('Необходимо указать имя и email сотрудника.')
      return false
    }

    this.isLoading = true
    try {
      const staffCollectionRef = collection(db, 'organizations', orgId, 'staff')
      const newStaffRef = doc(staffCollectionRef)

      const newStaffData = {
        name: staffData.name,
        avatar: staffData.avatar || '',
        email: staffData.email,
        position: staffData.position || 'Сотрудник',
        bio: { ru: '' },
        servicesIds: [],
        ...defaultWorkSchedule,
        createdAt: serverTimestamp(),
      }

      await setDoc(newStaffRef, newStaffData)

      const updatedStaffList = await this._getStaffList(orgId)
      runInAction(() => {
        this.staff = updatedStaffList
        this.errorStore.showSuccess(
          `Сотрудник ${staffData.name} успешно добавлен.`,
        )
      })
      return true
    } catch (error) {
      console.error('Ошибка при добавлении сотрудника:', error)
      this.errorStore.showError('Не удалось добавить сотрудника.')
      return false
    } finally {
      runInAction(() => {
        this.isLoading = false
      })
    }
  }

  async removeStaffMember(staffId) {
    const orgId = this.organizationStore.organization.id
    if (!orgId || !staffId) return

    const staffToRemove = this.staff.find((s) => s.id === staffId)
    if (!staffToRemove) {
      this.errorStore.showError('Сотрудник не найден.')
      return
    }
    if (staffToRemove.userId === this.organizationStore.organization.ownerId) {
      this.errorStore.showError('Владелец не может удалить сам себя.')
      return
    }

    this.isLoading = true
    try {
      const batch = writeBatch(db)
      const staffDocRef = doc(db, 'organizations', orgId, 'staff', staffId)
      batch.delete(staffDocRef)

      if (staffToRemove.userId) {
        const userDocRef = doc(db, 'users', staffToRemove.userId)
        batch.update(userDocRef, {
          organizationIds: arrayRemove(orgId),
          roles: arrayRemove('staff'),
          staffId: deleteField(),
        })
      }

      await batch.commit()

      runInAction(() => {
        this.staff = this.staff.filter((s) => s.id !== staffId)
        this.errorStore.showSuccess('Сотрудник удален.')
      })
    } catch (error) {
      console.error('Ошибка при удалении сотрудника:', error)
      this.errorStore.showError('Не удалось удалить сотрудника.')
    } finally {
      runInAction(() => {
        this.isLoading = false
      })
    }
  }

  /**
   * Находит и возвращает документ сотрудника, который является владельцем организации.
   */
  get ownerStaff() {
    if (
      !this.organizationStore.organization.ownerId ||
      this.staff.length === 0
    ) {
      return null
    }
    return this.staff.find(
      (s) => s.userId === this.organizationStore.organization.ownerId,
    )
  }

  /**
   * Возвращает ID документа сотрудника-владельца.
   */
  get ownerStaffId() {
    return this.ownerStaff?.id || null
  }
}

export default StaffStore
